import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useCookies } from "react-cookie";
import { ErrorBoundary } from "react-error-boundary";
import axios from "axios";
import DisableDevtool from "disable-devtool";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "react-query";

import Router from "./router";
import { ErrorPage } from "custom-components-v1";
import { ThemeStore } from "./context/ThemeStore";

import "./styles/common.scss";
import { ThemeProvider } from "styled-components";
import { useSelector } from "react-redux";
import { ConfigProvider, theme } from "antd";
import CacheInvalidator from "./components/global/CacheInvalidator";
import { setUser } from "./redux/slices/userSlice";
import { useDispatch } from "react-redux";

const queryClient = new QueryClient();
const UP = JSON.parse(localStorage.getItem("userProfile"));
const isOnboardingV2Done = UP ? UP.isOnboardingV2Done : false;
const isLms = sessionStorage.getItem("lms");
import "custom-components-v1/dist/index.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getReactCookieTokenOptions } from "./utils/tokenCookieOptions";

const PATHS_TO_IGNORE = ["/onboarding", "/auth", "/maintenance", "/auth/login", "/auth/reset/request", "/logout"];

const App = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [cookies, setCookies] = useCookies();
  const [searchParams] = useSearchParams();
  const cookieToken = cookies.token;

  useEffect(() => {
    const setCookieHeader = async () => {
      const urlToken = searchParams.get("t");

      if (urlToken) {
        setCookies("token", urlToken, getReactCookieTokenOptions());
        axios.defaults.headers.common["Authorization"] = `Bearer ${urlToken}`;
        window.localStorage.removeItem("userProfile");
      } else if (cookieToken) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${cookieToken}`;
      }
      if (window.location.pathname.includes("/auth") || window.location.pathname === "/analysis-demo") {
        return;
      }
      const res = await axios.get("/api/auth/user");
      dispatch(setUser(res?.data?.data));
    };
    setCookieHeader();
  }, [searchParams, cookieToken, setCookies, dispatch]);

  useEffect(() => {
    const handleMessage = (event) => {
      // Always validate the origin
      const allowedOrigins = [
        "http://localhost:3000",
        "http://localhost:3001",
        "https://test-staging.quizrr.in",
        "https://test.quizrr.in",
        "https://test-staging.quizrr.in",
        "https://app.quizrr.in",
      ];

      if (!allowedOrigins.includes(event.origin)) return;

      const { action, path } = event.data || {};

      // Handle reload and focus
      if (action === "reloadAndFocus") {
        window.focus();
        window.location.reload();
      }

      // Handle redirect
      if (action === "redirect" && path) {
        window.location.href = path;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // if (!UP || !UP.email.includes("@mathongo.com")) {
  //   DisableDevtool({
  //     ondevtoolopen: (type) => {
  //       const info = 'Devtools opened! Please close the devtools or open the page in a new tab';
  //       alert(info);
  //     },
  //     disableCopy: window.location.pathname !== "/auth" && window.location.pathname !== "/auth/login",
  //     disableCut: window.location.pathname !== "/auth" && window.location.pathname !== "/auth/login",
  //     disableSelect: window.location.pathname !== "/auth" && window.location.pathname !== "/auth/login",
  //     disablePaste: window.location.pathname !== "/auth" && window.location.pathname !== "/auth/login",
  //     md5: "09bf08f5b203b596a0160120219e6ff5",
  //   });
  // }

  const { isDark } = useSelector((state) => state.user);
  useEffect(() => {
    if (UP && !isOnboardingV2Done && PATHS_TO_IGNORE.indexOf(window.location.pathname) === -1 && !isLms) {
      navigate("/v2/home");
    }
  }, [navigate]);

  return (
    <ErrorBoundary
      onError={(error, componentStack) => {
        console.log({ error, componentStack });
      }}
      fallbackRender={({ resetErrorBoundary }) => <ErrorPage errorCode={503} resetErrorBoundary={resetErrorBoundary} />}
    >
      <ThemeStore>
        <CacheInvalidator />
        <Helmet>
          <meta charset="UTF-8" />
          <meta http-equiv="X-UA-Compatible" content="IE=edge" />
          <meta name="robots" content="imageindex, follow, snippet, index" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>{`IIT, JEE Mains | MathonGo`}</title>
          <link rel="manifest" href="/manifest.json" />
          <link href="/icons/favicon-16x16.png" rel="icon" type="image/png" sizes="16x16" />
          <link href="/icons/favicon-32x32.png" rel="icon" type="image/png" sizes="32x32" />
          <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png"></link>
          <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png"></link>
          <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png"></link>
          <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#2e005b"></link>
          <meta name="theme-color" content="#317EFB" />
          <link
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;800;900&display=swap"
            rel="stylesheet"
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Caveat:wght@400..700&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Reddit+Sans:ital,wght@0,200..900;1,200..900&display=swap"
            rel="stylesheet"
          />
        </Helmet>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider
            theme={{
              isDark: isDark,
            }}
          >
            <ConfigProvider
              theme={{
                algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
                token: isDark ? THEME.dark : THEME.light,
              }}
            >
              <Toaster />
              <Router />
            </ConfigProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </ThemeStore>
    </ErrorBoundary>
  );
};

const THEME = {
  light: {},
  dark: {
    colorBgContainer: "#152e4d",
    colorBgElevated: "#152e4d",
    colorText: "#fff",
  },
};

export default App;
